import { TelemetryType } from '~/db_types/swagger_types';
import { CosmicUserSupportContext } from '~/nasa_ui/types';
import { getActiveSupportContext } from '../mixins/CurrentUserMixin';
import { store } from '../store';
import { postTelemetry } from '../utils/dal';
import { getBrowserType } from '../utils/helpers/browser';
/**
 * Helper function that turns an Error object into a JSON.stringify-able object.
 *
 * Based on: https://stackoverflow.com/a/53624454/7977844
 *
 * @return object
 */
function jsonFriendlyErrorReplacer(value) {
    if (value instanceof Error) {
        return {
            // Explicitly pull Error's non-enumerable properties
            name: value.name,
            message: value.message,
            stack: value.stack
        };
    }
    return null;
}
const LAUNCHPAD_ERROR_SLUG = 'Error: Network error: Unexpected token';
/**
 * The Log module is used to save log message to the DB. It supports three types
 * of log information:
 *
 * - Info (Log.info)
 * - Error (Log.error)
 * - Telemetry (Log.telemetry)
 */
export default class Log {
    orgNodeIds = new Map([
        [CosmicUserSupportContext.ESOC, 'WyJvcmdhbml6YXRpb25zIiwiRVNPQyJd'],
        [CosmicUserSupportContext.xEMU, 'WyJvcmdhbml6YXRpb25zIiwieEVNVSJd'],
        [CosmicUserSupportContext.CTSD, 'WyJvcmdhbml6YXRpb25zIiwiQ1RTRCJd']
    ]);
    /**
     * Runs the create mutation.
     *
     * @param input - TelemetryRequest
     */
    async createTelemetryLog(input) {
        try {
            const request = {
                attributes: input.attributes,
                subType: input.subType,
                toUrl: input.toUrl,
                fromUrl: input.fromUrl,
                browserType: input.browserType,
                browserVersion: input.browserVersion
            };
            return await postTelemetry(request);
        }
        catch (err) {
            return;
        }
    }
    /**
     * Constructs and returns (a promise for) an ILogInput object with props that
     * are common to all three log types.
     *
     * @param subType - TelemetryType (INFO | ERROR | TELEMETRY)
     */
    async getBaseInput(subType) {
        // get support context (aka org code) from localStorage
        const orgCode = getActiveSupportContext();
        if (!orgCode) {
            return null;
        }
        // get the user object from localStorage
        const userObj = store.currentUser;
        const userId = userObj?.id;
        const browserType = getBrowserType;
        if (!userId) {
            return null;
        }
        return {
            browserType: browserType,
            subType: subType,
            attributes: {
                userObj,
                userId
            }
        };
    }
    /**
     * This method is used for logging Error objects.
     *
     * @param errorInput - { err: Error }
     */
    async error(errorInput) {
        const errString = errorInput.err.toString();
        // When the launchpad session times out it returns HTML here which throws the following error message
        if (errString.startsWith(LAUNCHPAD_ERROR_SLUG)) {
            return;
        }
        console.error(errString);
        // try/catch in case the await fails
        try {
            const input = await this.getBaseInput(TelemetryType.ERROR);
            const error = jsonFriendlyErrorReplacer(errorInput.err);
            if (!input || !error) {
                console.error('Failed to log error.');
                return null;
            }
            input.attributes.err = error;
            input.attributes.message = errString;
            input.fromUrl = window.location.href;
            input.browserType = getBrowserType()['type'];
            input.browserVersion = getBrowserType()['version'];
            return this.createTelemetryLog(input);
        }
        catch (err) {
            if (err instanceof Error) {
                const errString = err.toString();
                // When the launchpad session times out it returns HTML here which throws the following error message
                if (errString.startsWith(LAUNCHPAD_ERROR_SLUG)) {
                    return;
                }
            }
            return;
        }
    }
    /**
     * This method is used for logging telemetry information.
     *
     * @param telemetryInput - { fromUrl: string, toUrl: string }
     */
    async telemetry(telemetryInput) {
        // try/catch in case the await fails
        try {
            const input = await this.getBaseInput(TelemetryType.TELEMETRY);
            if (!input) {
                return null;
            }
            input.fromUrl = telemetryInput.fromUrl;
            input.toUrl = telemetryInput.toUrl;
            input.browserType = getBrowserType()['type'];
            input.browserVersion = getBrowserType()['version'];
            if (telemetryInput.message) {
                input.attributes.message = telemetryInput.message;
            }
            return this.createTelemetryLog(input);
        }
        catch (err) {
            return null;
        }
    }
}
